import { FetchResult } from "@apollo/client";
import {
  MetadataErrorFragment,
  OrderCancelMutationVariables,
  UpdateMetadataMutation,
  UpdateMetadataMutationVariables,
} from "@dashboard/graphql";

import {
  ORDER_CANCEL_REASON_CODE_METADATA_KEY,
  ORDER_CANCEL_REASON_METADATA_KEY,
} from "../components/OrderCancelDialog/reasons";

export interface CancelOrderWithReasonArgs {
  orderId: string;
  reasonCode: string;
  reason: string;
  updateMetadata: (
    variables: UpdateMetadataMutationVariables,
  ) => Promise<FetchResult<UpdateMetadataMutation>>;
  cancelOrder: (variables: OrderCancelMutationVariables) => Promise<unknown>;
}

/**
 * Stores the cancellation reason on the order, then cancels it.
 *
 * The two steps are sequential on purpose: Saleor builds the ORDER_CANCELLED webhook payload
 * when the cancel runs, so the reason has to be on the order before that. If the reason
 * cannot be stored the order is left untouched and the metadata errors are returned, so staff
 * never end up with a cancelled order and a cancellation email that carries no reason.
 */
export async function cancelOrderWithReason({
  orderId,
  reasonCode,
  reason,
  updateMetadata,
  cancelOrder,
}: CancelOrderWithReasonArgs): Promise<MetadataErrorFragment[]> {
  const result = await updateMetadata({
    id: orderId,
    input: [
      { key: ORDER_CANCEL_REASON_METADATA_KEY, value: reason },
      { key: ORDER_CANCEL_REASON_CODE_METADATA_KEY, value: reasonCode },
    ],
    keysToDelete: [],
  });
  const errors = [
    ...(result.data?.updateMetadata?.errors ?? []),
    ...(result.data?.deleteMetadata?.errors ?? []),
  ];

  if (errors.length > 0) {
    return errors;
  }

  await cancelOrder({ id: orderId });

  return [];
}
